import { Headline, Spinner, Stack } from '@servicetitan/design-system';
import styles from '../../CustomerDetails.module.css';
import { CustomerDetailsField } from '../CustomerDetailsField';
import currencyMask from '@lib/utils/maskCurrency';
import CustomerCardWrapper from '../CustomerCardWrapper/CustomerCardWrapper';
import { SpinnerStack } from '@pages/agent/InboxPage/components/CustomerDetails/components/CustomerContactMethods/CustomerContactMethods.styled';
import ErrorComponent from '@components/ErrorComponent';
import { strings as errorStrings } from '@components/ErrorComponent/strings';
import { useCustomerInformation } from '@api/queries/getCustomerById/useCustomerInformation';
import { getHttpResilienceErrorBody } from '@api/lib/apolloError';

interface CustomerInformationV2Props {
  customerId: string;
  tenantId: string;
}

export const CustomerInformationV2 = ({
  customerId,
  tenantId,
}: CustomerInformationV2Props) => {
  const { customerInformation, loading, error, refetch } =
    useCustomerInformation(tenantId, customerId);

  const customerType =
    customerInformation?.type === 'COMMERCIAL' ? 'Commercial' : 'Residential';

  return (
    <CustomerCardWrapper>
      <Stack className={styles.customerHeader} alignItems='center'>
        <Headline className='m-0' size='small'>
          Customer Information
        </Headline>
      </Stack>

      {loading && (
        <SpinnerStack justifyContent='center' alignItems='center'>
          <Spinner size='medium' />
        </SpinnerStack>
      )}

      {!loading && error && (
        <Stack className='w-100' justifyContent='center' alignItems='center'>
          <ErrorComponent
            noImage
            body={
              getHttpResilienceErrorBody(error) ?? errorStrings.defaultError
            }
            primaryAction={async () => {
              try {
                await refetch();
              } catch {
                // Failure stays on the query `error`; do not surface an unhandled rejection.
              }
            }}
            primaryActionLabel={errorStrings.labels.retry}
          />
        </Stack>
      )}

      {!loading && !error && (
        <Stack justifyContent='space-between' spacing={2}>
          <Stack direction='column' spacing={2}>
            <CustomerDetailsField title='Type' content={customerType} />
            <CustomerDetailsField
              title='Lifetime Revenue'
              content={currencyMask(
                customerInformation?.lifetimeRevenue ?? 0,
                true,
              )}
            />
          </Stack>
          <Stack direction='column' spacing={2}>
            <CustomerDetailsField
              title='Outstanding Balance'
              content={currencyMask(
                customerInformation?.outstandingBalance ?? 0,
              )}
            />
            <CustomerDetailsField
              title='Membership'
              content={
                customerInformation?.membership ? 'Is a member' : 'Not a member'
              }
            />
          </Stack>
        </Stack>
      )}
    </CustomerCardWrapper>
  );
};

export default CustomerInformationV2;
